'use client';

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type Factiii, FactiiiRequirement, type PinnedFactiii } from '@shared/all/domains/factiiis';
import type { Post } from '@shared/all/domains/posts';
import { type PostFeedInput } from '@shared/all/validators';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowRight, MoreVertical, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRunner } from '../../../hooks/useRunnerContext';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { client } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
import Confirmation from '../../common/Confirmation';
import { NewPostModal } from '../../posts/NewPost/NewPostModal';
import PostSkeletonLoading from '../../posts/PostSkeletonLoading';
import BoardCard from './BoardCard';
type Props = {
  factiii: Factiii;
  spaceSlug: string;
  // Visual clone of the dragged card shown at the insert position while a
  // card from another list is hovered over this list. The actual cache
  // transfer happens on drop, not during drag.
  ghost?: {
    post: Post;
    insertIndex: number;
    height: number;
  } | null;
  // Whether the viewer is allowed to add cards. Non-members see lists in
  // read-only mode.
  canPost: boolean;
  // Whether the viewer can move/reorder cards and lists (owner/moderator).
  canMove: boolean;
  // Whether the viewer can delete lists (owner only, server enforced).
  canDelete: boolean;
  allColumns: Factiii[];
  // Board search: the uuids of this column's cards that matched, or null when
  // no search is running (render everything). Resolved server-side by
  // BoardLayout so a match is never missed just because it wasn't on screen.
  matchedPostIds?: Set<string> | null;
  // Merge multi-select, owned by BoardLayout so it spans columns.
  selectedIds: string[];
  selectionHasChanges: boolean | null;
  onToggleSelect: (post: Post, factiiiId: number, hasChanges: boolean) => void;
};
const BoardList = ({
  factiii,
  spaceSlug,
  ghost,
  canPost,
  canMove,
  canDelete,
  allColumns,
  matchedPostIds,
  selectedIds,
  selectionHasChanges,
  onToggleSelect
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const {
    agent
  } = useRunner();
  const [newPostModalOpen, setNewPostModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'move';
    destId: number;
  } | {
    type: 'delete';
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const {
    queryKey
  } = usePostsShared(client, `${factiii.id}-${spaceSlug}`);
  const {
    width
  } = useWindowSize();
  const isMobile = width < 768;
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: `factiii-${factiii.id}`,
    data: {
      type: 'factiii',
      factiii: factiii,
      factiiiId: factiii.id
    },
    disabled: isMobile || !canMove
  });
  const {
    setNodeRef: setDroppableRef,
    isOver
  } = useDroppable({
    id: `factiii-${factiii.id}`,
    data: {
      type: 'factiii',
      factiii: factiii,
      factiiiId: factiii.id
    }
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1
  };
  const pinnedFactiii: PinnedFactiii = {
    id: factiii.id,
    name: factiii.name,
    slug: factiii.slug,
    spaceSlug: factiii.spaceSlug || spaceSlug,
    description: factiii.description || null,
    avatarKey: factiii.avatarKey,
    requirements: factiii.requirements || []
  };
  const handlePostCreated = () => {
    queryClient.invalidateQueries({
      queryKey
    });
    setNewPostModalOpen(false);
    agent?.broadcast({
      scope: 'posts',
      factiiiId: factiii.id
    });
  };
  const params: PostFeedInput = {
    source: 'space',
    spaceSlug,
    factiiiIds: [factiii.id],
    type: 'board'
  };

  //need it to be infinite query as thats what useposts work with
  const {
    data,
    isLoading
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({
      pageParam
    }) => {
      const {
        posts
      } = await unwrapFromResult(client.posts.feed.query({
        ...params,
        cursor: pageParam
      }));
      return {
        posts
      };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: () => {
      return undefined;
    },
    refetchOnWindowFocus: false,
    // Realtime patches keep the cache fresh; stop the 0-default from
    // refetching on every re-mount.
    staleTime: 5 * 60 * 1000,
    gcTime: 1000
  });
  const allPosts = data?.pages.flatMap(page => page.posts) || [];
  // Search narrows what this column draws; the cards themselves are the same
  // BoardCards, in the same order, so nothing about a card changes when found.
  const posts_0 = matchedPostIds ? allPosts.filter(post => matchedPostIds.has(post.id)) : allPosts;
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && menuBtnRef.current && !menuBtnRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);
  const handleBulkMove = async (destFactiiiId: number) => {
    setActionLoading(true);
    const results = await Promise.all(posts_0.map(post_0 => client.factiiis.tag.mutate({
      postId: post_0.id,
      removeFactiiiIds: [factiii.id],
      addFactiiis: [{
        factiiiId: destFactiiiId
      }]
    })));
    setActionLoading(false);
    setConfirmAction(null);
    queryClient.invalidateQueries({
      queryKey
    });
    // Same key shape as this list; only the trailing uniqueId differs.
    queryClient.invalidateQueries({
      queryKey: [...queryKey.slice(0, -1), `${destFactiiiId}-${spaceSlug}`]
    });
    const failed = results.filter(([error]) => error).length;
    if (failed > 0) {
      toast('Failed to move some cards', `${failed} card${failed !== 1 ? 's' : ''} could not be moved`, 'error');
    }
    toast(`Moved ${results.length - failed} cards`);
  };
  const handleDeleteList = async () => {
    setActionLoading(true);
    const [error_0] = await client.factiiis.delete.mutate({
      factiiiId: factiii.id,
      spaceSlug
    });
    setActionLoading(false);
    setConfirmAction(null);
    if (error_0) {
      toast('Failed to delete list', '', 'error');
      return;
    }
    queryClient.invalidateQueries({
      queryKey: ['boardFactiiis', spaceSlug]
    });
    agent?.broadcast({
      scope: 'factiiis'
    });
  };

  // Cards can only move to other board columns (LIST factiiis), never to a
  // plain non-list factiii that happens to be in the space.
  const destColumns = allColumns.filter(c => c.id !== factiii.id && (c.requirements ?? []).includes(FactiiiRequirement.LIST));
  const virtualizer = useVirtualizer({
    count: posts_0.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 120,
    overscan: 5,
    // Key rows by post identity, not index, so a removal can't hand a
    // recycled BoardCard another post's state.
    getItemKey: index => posts_0[index]?.id ?? index
  });
  const virtualItems = virtualizer.getVirtualItems();
  return <>
      <div ref={node => {
      setSortableRef(node);
      setDroppableRef(node);
    }} style={style} data-id="board-list" {...attributes} {...listeners} className={`xs:w-64 group flex max-h-full w-[75vw] shrink-0 flex-col rounded-xl bg-background-secondary shadow-md transition-all duration-200 sm:w-72 md:w-80 ${isOver ? 'ring-2 ring-primary ring-offset-2 ring-offset-primary/10' : ''} ${isDragging ? 'cursor-grabbing opacity-75' : isMobile ? '' : 'cursor-grab'}`}>
        {/* List Header */}
        <div className="flex items-center justify-between border-b border-ds-border-subtle px-3 py-2.5">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <Avatar factiiiAvatarKey={factiii.avatarKey ?? null} factiiiId={factiii.id} size={24} />
            <h3 className="truncate text-sm font-semibold text-textPrimary">
              {factiii.name || 'Unnamed Factiii'}
            </h3>
          </div>
          <div className="ml-2 flex items-center gap-1">
            <span className="text-xs text-textSecondary">{posts_0.length}</span>
            {(canMove && destColumns.length > 0 || canDelete) && <button ref={menuBtnRef} onClick={() => setMenuOpen(prev => !prev)} onMouseDown={e_0 => e_0.stopPropagation()} onPointerDown={e_1 => e_1.stopPropagation()} className="rounded p-0.5 text-textSecondary/60 opacity-0 transition-all hover:bg-background-light hover:text-textPrimary group-hover:opacity-100">
                <MoreVertical size={14} />
              </button>}
          </div>
        </div>

        {/* Posts Container - Scrollable. Content-sized (flex-initial) so
            Add a card sits right under the last card; shrinks and scrolls
            only when the cards overflow the column's max-h cap. */}
        <div ref={scrollRef} className="min-h-0 flex-initial overflow-y-auto overflow-x-hidden">
          {isLoading && posts_0.length === 0 ? <div className="space-y-2 p-2">
              <PostSkeletonLoading />
              <PostSkeletonLoading />
            </div> : posts_0.length === 0 ? ghost ? <div className="p-2">
                <BoardCard post={ghost.post} factiiiId={factiii.id} spaceSlug={spaceSlug} isGhost />
              </div> : <div className="flex flex-col items-center justify-center py-6 text-center">
                <p className="mb-3 text-xs text-textSecondary">
                  No cards in this list
                </p>
                {canPost && <button onClick={() => setNewPostModalOpen(true)} className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-emphasis text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary">
                    <Plus size={14} />
                    Add a card
                  </button>}
              </div> : <SortableContext items={posts_0.map(post_1 => `post-${post_1.id}`)} strategy={verticalListSortingStrategy}>
              <div style={{
            height: `${virtualizer.getTotalSize() + (ghost?.height ?? 0)}px`,
            width: '100%',
            position: 'relative'
          }}>
                {virtualItems.map(virtualItem => {
              const post_2 = posts_0[virtualItem.index];
              if (!post_2) return null;
              const ghostShift = ghost && virtualItem.index >= ghost.insertIndex ? ghost.height : 0;
              return <div key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement} style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start + ghostShift}px)`,
                transition: 'transform 150ms ease'
              }}>
                      <div className="p-2">
                        <BoardCard post={post_2} factiiiId={factiii.id} spaceSlug={spaceSlug} canMove={canMove} selectable={canMove} selected={selectedIds.includes(post_2.id)} selectionHasChanges={selectionHasChanges} onToggleSelect={hasChanges => onToggleSelect(post_2, factiii.id, hasChanges)} />
                      </div>
                    </div>;
            })}
                {ghost && <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${ghost.insertIndex < virtualItems.length ? virtualItems[ghost.insertIndex]?.start ?? 0 : virtualizer.getTotalSize()}px)`,
              pointerEvents: 'none'
            }}>
                    <div className="p-2">
                      <BoardCard post={ghost.post} factiiiId={factiii.id} spaceSlug={spaceSlug} isGhost />
                    </div>
                  </div>}
              </div>
            </SortableContext>}
        </div>

        {/* Add Card Button - Always visible at bottom */}
        {canPost && <div className="p-2">
            <button data-id="board-add-card" onClick={() => setNewPostModalOpen(true)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary">
              <Plus size={16} />
              <span>Add a card</span>
            </button>
          </div>}
      </div>

      {/* New Post Modal */}
      {newPostModalOpen && <NewPostModal closeHandler={() => setNewPostModalOpen(false)} spaceSlug={spaceSlug} pinnedFactiii={pinnedFactiii} onPostCreated={handlePostCreated} uniqueId={`${factiii.id}-${spaceSlug}`} isBoardPost />}

      {/* Column dropdown menu */}
      {menuOpen && menuBtnRef.current && typeof document !== 'undefined' && createPortal(<div ref={menuRef} className="z-popover" style={{
      position: 'fixed',
      top: menuBtnRef.current.getBoundingClientRect().bottom + 4,
      left: menuBtnRef.current.getBoundingClientRect().left
    }} onMouseDown={e_2 => e_2.stopPropagation()} onPointerDown={e_3 => e_3.stopPropagation()}>
            <div className="min-w-48 rounded-md border border-ds-border-standard bg-background-primary p-1.5 shadow-lg">
              {canMove && destColumns.length > 0 && <>
                  <p className="mb-1 px-2 py-1 text-xs font-emphasis text-textSecondary/60">
                    Move all cards to
                  </p>
                  {destColumns.map(col => <button key={col.id} onClick={() => {
            setConfirmAction({
              type: 'move',
              destId: col.id
            });
            setMenuOpen(false);
          }} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary">
                      <ArrowRight size={14} className="shrink-0" />
                      <span className="truncate">{col.name}</span>
                    </button>)}
                </>}
              {canDelete && <>
                  {canMove && destColumns.length > 0 && <div className="my-1.5 border-t border-ds-border-subtle" />}
                  <button onClick={() => {
            setConfirmAction({
              type: 'delete'
            });
            setMenuOpen(false);
          }} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-destructive transition-colors hover:bg-background-light">
                    <Trash2 size={14} className="shrink-0" />
                    <span>Delete list</span>
                  </button>
                </>}
            </div>
          </div>, document.body)}

      {/* Bulk move confirmation */}
      {confirmAction?.type === 'move' && (() => {
      const destColumn = allColumns.find(c_0 => c_0.id === confirmAction.destId);
      return <Confirmation title="Move all cards" message={`Move all ${posts_0.length} card${posts_0.length !== 1 ? 's' : ''} from "${factiii.name}" to "${destColumn?.name ?? 'Unknown'}"?`} actionButtonText="Move All" confirmHandler={() => handleBulkMove(confirmAction.destId)} cancelHandler={() => setConfirmAction(null)} actionButtonLoading={actionLoading} />;
    })()}

      {/* Delete list confirmation */}
      {confirmAction?.type === 'delete' && <Confirmation title="Delete list?" message={posts_0.length > 0 ? `"${factiii.name || 'This list'}" and its ${posts_0.length} card${posts_0.length !== 1 ? 's' : ''} will be deleted, along with any replies on them. To keep any, cancel and use "Move all cards to" first. This can't be undone.` : `"${factiii.name || 'This list'}" will be deleted. This can't be undone.`} actionButtonText="Delete" confirmHandler={() => {
      void handleDeleteList();
    }} cancelHandler={() => setConfirmAction(null)} actionButtonLoading={actionLoading} />}
    </>;
};
export default BoardList;